// src/app/modules/user/components/forms/ManualBPEntryForm.tsx

import React, {useState} from 'react'
import axios from 'axios'
import moment from 'moment'
import {toast} from 'react-toastify'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import PrimaryButton from '../common/buttons/PrimaryButton'
import {useHealthAlertNotification} from '../../context/NotificationContext'
import {NotificationModel, ApiResponseModel} from '../../../../entities/entities'
// Redux imports for refreshing patient data after BP reading
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { AppDispatch } from '../../../../../store/store'
import { refreshPatientAfterDeviceChange } from '../../../../../store/slices/patientSlice'
import {useReduxAuth} from '../../../auth'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'

interface ManualBPEntryFormProps {
  /** UUID of the patient */
  patientUUID: string
  /** ID of the doctor (current user) */
  doctorId: string
  /** Callback after successful submission */
  onSuccess?: () => void
}

const ManualBPEntryForm: React.FC<ManualBPEntryFormProps> = ({
  patientUUID,
  doctorId,
  onSuccess,
}) => {
  const {setHealthAlertNotification} = useHealthAlertNotification()
  const dispatch = useDispatch<AppDispatch>()
  const {patientId} = useParams<{patientId: string}>()
  const {currentUser} = useReduxAuth()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined

  const [sys, setSys] = useState<string>('')
  const [dia, setDia] = useState<string>('')
  const [pulse, setPulse] = useState<string>('')
  const [readingTime, setReadingTime] = useState<string>(moment().format('YYYY-MM-DDTHH:mm'))
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const s = Number(sys),
      d = Number(dia),
      p = Number(pulse)
    if (!s || !d || !p) {
      return toast.error('All of “Systolic”, “Diastolic” and “Pulse” must be provided as numbers.')
    }

    let payload: any = {sys: s, dia: d, pulse: p}
    if (readingTime) {
      const dt = new Date(readingTime)
      if (isNaN(dt.getTime())) {
        return toast.error('Please provide a valid date-time.')
      }
      payload.reading_taken_at = dt.toISOString()
    }

    setSubmitting(true)
    try {
      const url = API_ENDPOINTS.CREATE_MANUAL_BP_ENTRY(patientUUID, doctorId)
      const resp = await axios.post(url, payload)
      toast.success(resp.data.message)
      setSys('')
      setDia('')
      setPulse('')
      setReadingTime(moment().format('YYYY-MM-DDTHH:mm'))
      onSuccess?.()
      fetchAndUpdateNotifications()
      
      // Refresh Redux patient state to update PatientDetailBadges readings count
      if (patientId) {
        const doctorIdForRefresh = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maSelectedDoctorId : undefined
        dispatch(refreshPatientAfterDeviceChange({ patientUuid: patientId, doctorId: doctorIdForRefresh }))
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save reading.')
    } finally {
      setSubmitting(false)
    }
  }

  const fetchAndUpdateNotifications = async () => {
    try {
      const queryParams = `&get_notifications=${true}`
      const response = await axios.get<ApiResponseModel<{alerts: NotificationModel[]}>>(
        API_ENDPOINTS.GET_ALL_HEALTH_ALERTS(queryParams)
      )
      const allHealthAlerts = response.data?.data?.alerts || []
      setHealthAlertNotification(allHealthAlerts)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        console.error(error.message)
      } else {
        console.error('An unexpected error occurred')
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className='border p-4 rounded-5 bg-white shadow-sm'>
      <div className='row g-3'>
        <div className='col-sm-4'>
          <label className='form-label'>Systolic (mmHg)</label>
          <input
            type='number'
            className='form-control'
            value={sys}
            onChange={(e) => setSys(e.target.value)}
            min={0}
          />
        </div>
        <div className='col-sm-4'>
          <label className='form-label'>Diastolic (mmHg)</label>
          <input
            type='number'
            className='form-control'
            value={dia}
            onChange={(e) => setDia(e.target.value)}
            min={0}
          />
        </div>
        <div className='col-sm-4'>
          <label className='form-label'>Pulse (bpm)</label>
          <input
            type='number'
            className='form-control'
            value={pulse}
            onChange={(e) => setPulse(e.target.value)}
            min={0}
          />
        </div>
      </div>

      <div className='mt-3'>
        <label className='form-label'>When taken</label>
        <input
          type='datetime-local'
          className='form-control'
          value={readingTime}
          onChange={(e) => setReadingTime(e.target.value)}
          required
          max={moment().format('YYYY-MM-DDTHH:mm')}
        />
      </div>

      <div className='mt-4 d-flex justify-content-end'>
        <PrimaryButton type='submit' className='bg-primary' disabled={submitting}>
          {submitting ? 'Saving…' : 'Save Entry'}
        </PrimaryButton>
      </div>
    </form>
  )
}

export default ManualBPEntryForm
